import { WebGivingMerchant, MerchantCardPreference, ApplePayPaymentMethodType, ApplePayPaymentPassActivationState, ApplePayInfo, ApplePayBillingContact, FrequencyCode } from '../webgiving-generated';
import { getCardBrandsFromKeys } from './card-utils';

export enum ApplePayMerchantCapabilities {
    Supports3DS = "supports3DS",
    SupportsCredit = "supportsCredit",
    SupportsDebit = "supportsDebit"
}

export enum ApplePaySupportedNetworks {
    Amex = 'amex',
    Discover = 'discover',
    MasterCard = 'masterCard',
    VISA = 'visa',
}

export function isApplePayCancelError(error: any) {
    if ((error as ApplePayJS.Event) && (error as ApplePayJS.Event).type === 'cancel') {
        return true;
    }
    return false;
}

export function getApplePayNetworksAndCapabilities(merchant: WebGivingMerchant) {
    const { SupportedCardBrandKeys, CardPreference } = merchant;
    const supportsCards = SupportedCardBrandKeys && SupportedCardBrandKeys.length > 0;

    if (supportsCards) {
        let merchantCapabilities: ApplePayMerchantCapabilities[] = [];
        switch (CardPreference) {
            case MerchantCardPreference.AnyCard:
            case MerchantCardPreference.DebitPreferred:
                merchantCapabilities = [
                    ApplePayMerchantCapabilities.Supports3DS,
                    ApplePayMerchantCapabilities.SupportsCredit,
                    ApplePayMerchantCapabilities.SupportsDebit,
                ];
                break;
            case MerchantCardPreference.DebitOnly:
                merchantCapabilities = [
                    ApplePayMerchantCapabilities.Supports3DS,
                    ApplePayMerchantCapabilities.SupportsDebit,
                ];
                break;
        }

		const supportedNetworks = getCardBrandsFromKeys(SupportedCardBrandKeys).map(
			(b) => (b.shortName as ApplePaySupportedNetworks)
		);

        return {
            supportedNetworks: supportedNetworks,
            merchantCapabilities,
        };
	}

	return false;
}

export const mapApplePaymentMethodType = (type: ApplePayJS.ApplePayPaymentMethodType) => {
    switch (type) {
        case 'debit':
            return ApplePayPaymentMethodType.Debit;
        case 'prepaid':
            return ApplePayPaymentMethodType.Prepaid
        case 'credit':
            return ApplePayPaymentMethodType.Credit
        case 'store':
            return ApplePayPaymentMethodType.Store
        default:
            return type as ApplePayPaymentMethodType
    }
}

export const mapApplePayPaymentPassActivationState = (type: ApplePayJS.ApplePayPaymentPassActivationState) => {
    switch (type) {
        case 'activated':
            return ApplePayPaymentPassActivationState.Activated;
        case 'deactivated':
            return ApplePayPaymentPassActivationState.Deactivated
        case 'activating':
            return ApplePayPaymentPassActivationState.Activating
        case 'requiresActivation':
            return ApplePayPaymentPassActivationState.RequiresActivation
        case 'suspended':
            return ApplePayPaymentPassActivationState.Suspended
        default:
            return type as ApplePayPaymentPassActivationState
    }
}

export const mapFrequencyCodeToApplePayInterval = (frequencyCode: FrequencyCode) : { intervalUnit: ApplePayJS.ApplePayRecurringPaymentDateUnit, intervalCount: number } => {
    switch(frequencyCode) {
        case FrequencyCode.OneMinute:
            return { intervalUnit: 'minute', intervalCount: 1 };
        case FrequencyCode.FiveMinutes:
            return { intervalUnit: 'minute', intervalCount: 5 };
        case FrequencyCode.FourHours:
            return { intervalUnit: 'hour', intervalCount: 4 };
        case FrequencyCode.Weekly:
            return { intervalUnit: 'day', intervalCount: 7 };
        case FrequencyCode.Fortnightly:
            return { intervalUnit: 'day', intervalCount: 14 };
		case FrequencyCode.FirstAndFifteenth:
			return { intervalUnit: 'day', intervalCount: 15 };
        case FrequencyCode.Monthly:
            return { intervalUnit: 'month', intervalCount: 1 };
        case FrequencyCode.Quarterly:
            return { intervalUnit: 'month', intervalCount: 3 };
        case FrequencyCode.SemiYearly:
            return { intervalUnit: 'month', intervalCount: 6 };
        case FrequencyCode.Yearly:
            return { intervalUnit: 'year', intervalCount: 1 };
        default:
            // For FrequencyCode.FirstAndFifteenth? Perhaps: { intervalUnit: 'day', intervalCount: 15 }
            throw new Error(`Frequency code ${frequencyCode} not supported by Apple Pay`);
    }
}

export function getApplePayInfo({ token, billingContact }: ApplePayJS.ApplePayPayment): ApplePayInfo {
    const {
        paymentData,
        paymentMethod: {
            displayName,
            network,
            type,
            paymentPass
        },
        transactionIdentifier
    } = token;

    const billingAddress = {} as ApplePayBillingContact;

    if (billingContact) {
        billingAddress.AddressLine1 = billingContact.addressLines
            && billingContact.addressLines.length >= 1
            && billingContact.addressLines[0] || '';
        billingAddress.AddressLine2 = billingContact.addressLines
            && billingContact.addressLines.length >= 2
            && billingContact.addressLines[1] || '';
        billingAddress.City = billingContact.locality || '',
            billingAddress.State = billingContact.administrativeArea || '';
        billingAddress.Zip = billingContact.postalCode || '',
            billingAddress.Country = billingContact.countryCode || '';
    };

    const requestInfo: ApplePayInfo = {
        ApplePayPaymentToken: {
            PaymentData: JSON.stringify(paymentData),
            PaymentMethod: {
                DisplayName: displayName,
                Network: network,
                Type: mapApplePaymentMethodType(type),
                PaymentPass: {
                    PrimaryAccountIdentifier: '',
                    PrimaryAccountNumberSuffix: '',
                    DeviceAccountIdentifier: null,
                    DeviceAccountNumberSuffix: null,
                    ActivationState: ApplePayPaymentPassActivationState.Activated
                },
            },
            TransactionIdentifier: transactionIdentifier
        },
        ApplePayBillingContact: billingAddress
    };

    if (paymentPass) {
        requestInfo.ApplePayPaymentToken.PaymentMethod.PaymentPass = {
            ActivationState: mapApplePayPaymentPassActivationState(paymentPass.activationState),
            DeviceAccountIdentifier: paymentPass.deviceAccountIdentifier || null,
            DeviceAccountNumberSuffix: paymentPass.deviceAccountNumberSuffix || null,
            PrimaryAccountIdentifier: paymentPass.primaryAccountIdentifier,
            PrimaryAccountNumberSuffix: paymentPass.primaryAccountNumberSuffix,
        };
    }

    return requestInfo;
}

